import { createContext, useContext, useState } from "react";

export const translations = {
  fr: {
    nav: {
      home: "Accueil",
      products: "Produits",
      windows: "Fenêtres",
      doors: "Portes d'entrée",
      garage: "Portes de garage",
      projects: "Réalisations",
      primes: "Primes régionales",
      about: "À propos",
      quote: "Prendre rendez-vous",
    },
    hero: {
      overline: "Menuiserie intérieure et extérieure",
      title1: "Votre vision.",
      title2: "Notre savoir-faire.",
      subtitle: "Menuiseries sur mesure, pensées pour votre habitat.",
      expertisesTitle: "Nos expertises",
      cta1: "Découvrir nos solutions",
      cta2: "Prendre rendez-vous",
      cta3: "Vérifiez vos primes régionales en quelques clics",
      cta3Short: "Vérifiez vos primes régionales",
      quoteCta: "Demandez votre devis",
      scroll: "Défiler",
    },
    home: {
      intro: {
        overline: "L'entreprise",
        title: "La menuiserie comme geste architectural.",
        text: "Basés en Belgique, nous concevons, fabriquons et posons des menuiseries sur mesure. Une seule exigence : la justesse du détail, de la première mesure à la pose finale.",
      },
      categories: {
        overline: "Nos produits",
        title: "Quatre expertises, une même précision.",
        view: "Découvrir",
      },
      projects: {
        overline: "Réalisations",
        title: "Des chantiers, pas des rendus.",
        cta: "Voir toutes les réalisations",
      },
      service: {
        overline: "Un service complet",
        title: "De la prise de mesures à la pose.",
        text: "Nous vous accompagnons à chaque étape de votre projet, partout en Belgique.",
        steps: [
          { n: "01", title: "Conseil & prise de mesures", text: "Nous étudions votre projet et réalisons les mesures nécessaires sur place." },
          { n: "02", title: "Fourniture sur mesure", text: "Des solutions sélectionnées et configurées selon votre habitation." },
          { n: "03", title: "Pose professionnelle", text: "Une installation soignée jusque dans les finitions." },
          { n: "04", title: "Partout en Belgique", text: "Nous réalisons vos projets dans les trois régions du pays." },
        ],
      },

      primes: {
        overline: "Primes & aides",
        title: "Jusqu'à 50 % de primes.",
        text: "Selon votre situation, le remplacement de vos menuiseries peut ouvrir droit à des primes rénovation et énergie.",
        cta: "Découvrir les primes",
      },
      finalCta: {
        overline: "Votre projet",
        title: "Maintenant, parlons de votre projet.",
        text: "Recevez une estimation sur mesure sous 48 heures.",
        cta: "Prendre rendez-vous",
        heading: "Un projet en tête ?",
        subtitle: "Parlons-en et trouvons ensemble la solution adaptée à votre habitation.",
        primary: "Demander un devis",
        secondary: "Prendre rendez-vous",
      },
    },
    productsPage: {
      overline: "Produits",
      title: "Un catalogue architectural.",
      lead: "Fenêtres, portes et portes de garage sur mesure, pensés pour durer et sublimer chaque façade.",
      view: "Voir le produit",
    },
    fenetresPage: {
      hero: {
        overline: "Fenêtres",
        title1: "Des fenêtres",
        title2: "pensées pour durer.",
        subtitle:
          "Performance thermique, confort acoustique et lignes épurées : des menuiseries conçues pour sublimer votre architecture et traverser les années.",
      },
      materials: {
        overline: "Les matériaux",
        title: "PVC ou aluminium, selon votre projet.",
        text: "Vision+ propose différentes solutions selon votre projet, votre style architectural et vos exigences de performance. Chaque matériau a ses forces — nous vous aidons à choisir le bon.",
        pvc: { name: "PVC", desc: "Robuste, isolant et économique. Idéal pour la rénovation comme pour le neuf." },
        alu: { name: "Aluminium", desc: "Fin, contemporain et durable. Pour les grandes ouvertures et les lignes affirmées." },
      },
      systems: {
        overline: "Nos systèmes de fenêtres et portes d'entrée",
        title: "Trois systèmes, une même exigence.",
        specsLabel: "Caractéristiques",
        items: [
          {
            name: "Aluplast Ideal 4000",
            material: "PVC",
            glazing: "Double vitrage",
            position:
              "Notre solution PVC polyvalente, particulièrement adaptée à la rénovation comme aux logements individuels et collectifs modernes.",
            specs: [
              "Profilé de classe B",
              "Profondeur d'installation : 70 mm",
              "Structure : 5 chambres",
              "Vitrage jusqu'à 42 mm",
              "Uw = 1,3 W/m²K",
              "Vitrage standard : 4/16Ar/4th [Ug=1.1]",
              "2 joints d'étanchéité",
              "Cadre rénovation ou monobloc possible",
            ],
          },
          {
            name: "Aluplast Ideal 8000",
            material: "PVC",
            glazing: "Triple vitrage",
            position:
              "Notre solution PVC haute performance à triple vitrage, pour les projets qui privilégient l'isolation thermique, le confort acoustique et l'efficacité énergétique.",
            specs: [
              "Profilé de classe B",
              "Profondeur d'installation : 85 mm",
              "Structure : 6 chambres",
              "Vitrage jusqu'à 52 mm",
              "Uw = 0,74 W/m²K",
              "Vitrage standard : 4th/18Ar/4/18Ar/4th [Ug=0,5]",
              "3 joints d'étanchéité",
              "Triple vitrage",
            ],
          },
          {
            name: "Aluprof MB-86N",
            material: "Aluminium",
            glazing: "Triple vitrage",
            position:
              "Notre solution aluminium premium à triple vitrage, alliant esthétique architecturale contemporaine et excellentes performances thermiques.",
            specs: [
              "Profondeur d'installation : 77 mm",
              "Structure : 3 chambres",
              "Vitrage jusqu'à 70 mm",
              "Uw = 0,77 W/m²K",
              "Vitrage standard : 4th/18Ar/4/18Ar/4th [Ug=0,5]",
              "2 joints d'étanchéité",
              "Triple vitrage",
            ],
          },
        ],
      },
      performance: {
        overline: "Performance & confort",
        title: "Ce qui compte vraiment.",
        items: [
          { t: "Isolation thermique", d: "Des profilés multi-chambres et vitrages performants pour retenir la chaleur." },
          { t: "Isolation acoustique", d: "Le calme chez soi, même en environnement urbain." },
          { t: "Confort", d: "Lumière généreuse et manipulation fluide au quotidien." },
          { t: "Sécurité", d: "Ferrures et vitrages pensés pour protéger durablement." },
          { t: "Esthétique", d: "Des lignes fines qui s'effacent devant l'architecture." },
        ],
      },
      colours: {
        overline: "Couleurs & finitions",
        title: "Adaptées à votre architecture.",
        text: "Selon le système choisi, différentes couleurs et finitions sont disponibles pour accorder vos fenêtres à votre façade et à votre intérieur.",
        applies: "Finitions disponibles pour les systèmes PVC Aluplast (Ideal 4000 & 8000).",
        refPlaceholder: "Réf. à confirmer",
        placeholderNote: "Aperçu indicatif — le nuancier officiel Aluplast sera intégré prochainement.",
        download: "Télécharger le nuancier complet",
        viewAll: "Voir toutes les couleurs",
        collapse: "Réduire",
      },
      realisations: { overline: "Réalisations", title: "Nos fenêtres en situation.", cta: "Voir toutes les réalisations" },
      cta: { title: "Parlons de votre projet.", button: "Prendre rendez-vous" },
    },
    garagePage: {
      systems: {
        overline: "Nos systèmes de portes de garage",
        specsLabel: "Caractéristiques",
        items: [
          {
            name: "Porte de garage sectionnelle",
            material: "INFINITI",
            glazing: "",
            position:
              "Porte sectionnelle isolée à panneau de 40 mm, disponible en versions Quiet, Safe et Premium.",
            specs: [
              "Options disponibles : Quiet, Safe, Premium",
              "Épaisseur du panneau : 40 mm",
              "Disponible en version : standard et thermique",
              "Poids maximal : 130 kg",
              "Dimensions minimales des écoinçons : 90 mm",
              "Dimensions minimales du linteau : 105 mm",
              "Panneaux disponibles avec surface lisse ou texture woodgrain, avec rainures basses et hautes, laqués selon le nuancier RAL ou plaxés",
              "Recommandée pour l'installation dans les petits garages",
              "Installation rapide ne nécessitant pas de ressorts spéciaux",
              "Mécanisme à double ressort",
            ],
          },
          {
            name: "Portes de garage battantes",
            material: "PRESTO",
            glazing: "",
            position:
              "Portes battantes en acier robuste, garnies d'un panneau isolant de 40 mm en polyuréthane.",
            specs: [
              "Profilé en acier : 1,5 mm",
              "Remplissage avec un panneau de porte sectionnelle de 40 mm",
              "Isolation du panneau avec de la mousse de polyuréthane",
              "Seuil en acier ou automatique",
              "Profilés laqués dans les couleurs du nuancier RAL",
            ],
          },
          {
            name: "Porte de garage enroulable",
            material: "BALUPROF",
            glazing: "",
            position:
              "Porte enroulable compacte à tablier PA 55 ou PA 77, avec motorisation tubulaire ou latérale.",
            specs: [
              "Tablier : PA 55 ou PA 77",
              "Choix de motorisation tubulaire ou latérale",
              "Porte dans un coffre ou sur des consoles",
              "Protection contre la chute incontrôlée du tablier (frein à inertie) en standard",
              "Barre palpeuse optique ou cellules photoélectriques en option",
              "Large gamme de couleurs pour le coffre, les coulisses et le tablier",
              "Lames hublots et ventilation en option",
              "Manœuvre de secours en standard",
            ],
          },
        ],
      },
      colours: {
        overline: "Couleurs & finitions",
        text: "Selon le système choisi, différentes couleurs et finitions sont disponibles pour accorder votre garage à votre façade et à vos fenêtres.",
        applies: "Finitions disponibles pour les systèmes de portes de garage sectionnelles.",
        download: "Télécharger le nuancier complet",
        viewAll: "Voir toutes les couleurs",
        collapse: "Réduire",
      },
    },
    cloturesPage: {
      systems: {
        overline: "Quelques exemples de nos clôtures",
        specsLabel: "Caractéristiques",
        items: [
          {
            name: "Lugano",
            material: "Clôtures standards",
            glazing: "",
            position:
              "Clôture standard en acier, remplissage motif EK.20.102. Portillon, portail coulissant ou à deux vantaux, et travée.",
            specs: [
              "Motif de remplissage : EK.20.102",
              "Matériau : acier",
              "Système de clôture : portillon, portail coulissant ou portail à deux vantaux, travée",
              "Dimension des poteaux : 80 × 80 ; 100 × 100 mm (portillon + portail)",
              "Profils du cadre : 80 × 40 mm",
              "Profils de remplissage : 80 × 20 mm",
              "Couleurs : RAL – texture",
            ],
          },
          {
            name: "Maulazzo",
            material: "Clôtures élégantes",
            glazing: "",
            position:
              "Clôture élégante en aluminium, panneau EK.20.108 posable à l'horizontale ou à la verticale.",
            specs: [
              "Modèle de remplissage : EK.20.108",
              "Matériau : aluminium",
              "Système de clôture : portillon, portail coulissant ou portail à deux vantaux, travée",
              "Dimension des poteaux : 80 × 80 ; 100 × 100 mm (portillon + portail)",
              "Profils du cadre : 58 × 44 mm",
              "Profilés de remplissage : panneau en aluminium, jonction avec lamelle, rainure de 200 mm",
              "Le panneau peut être inséré horizontalement ou verticalement",
              "Couleurs RAL : couleurs mates et texturées",
              "Panneaux alu Winchester et option Bicolor disponibles",
            ],
          },
          {
            name: "Bolsena",
            material: "Clôtures de prestige",
            glazing: "",
            position:
              "Clôture de prestige en acier, remplissage EK.20.110 aux profils variés.",
            specs: [
              "Modèle de remplissage : EK.20.110",
              "Matériau : acier",
              "Système de clôture : portillon, portail coulissant ou portail à deux vantaux, travée",
              "Dimension des poteaux : 80 × 80 ; 100 × 100 mm (portillon + portail)",
              "Profils du cadre : 60 × 40 mm",
              "Profils de remplissage : 80 × 20 ; 60 × 30 ; 30 × 20 mm",
              "Couleurs : RAL – texture",
            ],
          },
        ],
      },
      colours: {
        overline: "Couleurs & finitions",
        text: "Nos clôtures sont disponibles dans un très large choix de couleurs et de finitions. En raison du nombre important de possibilités, nous vous invitons à consulter notre nuancier complet afin de découvrir l'ensemble des teintes disponibles ainsi que notre catalogue complet.",
        applies: "",
        download: "Télécharger le nuancier complet",
        downloadCatalogue: "Télécharger le catalogue complet",
        viewAll: "Voir toutes les couleurs",
        collapse: "Réduire",
      },
    },
    accessoiresPage: {
      systems: {
        overline: "Nos systèmes de protections solaires extérieures",
        specsLabel: "Caractéristiques",
        items: [
          {
            name: "Volet roulant",
            material: "SK-45",
            glazing: "",
            position:
              "Grâce à la forme universelle du caisson, le store peut être monté aussi bien directement sur la façade d'un bâtiment que dans une niche du dormant de la fenêtre.",
            specs: [
              "Type de tablier : aluminium rempli de mousse de polyuréthane ou PVC",
              "Révision du côté extérieur",
              "Coulisses : aluminium",
              "Résistance aux intempéries",
              "Largeur minimale du caisson : 400 mm",
            ],
          },
          {
            name: "Screens",
            material: "FME-90",
            glazing: "",
            position:
              "Principalement utilisé pour réduire efficacement la quantité de lumière et de chaleur entrant à l'intérieur des pièces, il n'y a pas d'espace entre le tissu du store et les coulisses.",
            specs: [
              "Type de store : ZIP",
              "Type de coffre : rénovation, rénovation inversée (contra) ou encastré",
              "Moteur : filaire ou radio",
              "Matériau : fibre de verre + PVC",
              "Dimensions maximales (L × H) : 6000 × 5000 mm",
              "Surface maximale : 18 m²",
              "Coffre et rails thermolaqués dans les couleurs du nuancier RAL",
            ],
          },
          {
            name: "Brise-soleil orientable",
            material: "BSO",
            glazing: "",
            position:
              "Il peut être facilement installé dans la plupart des maisons, dans un système adaptatif ou encastré.",
            specs: [
              "Trappe de révision : aucune",
              "Coulisses : aluminium",
              "Cornières / supports de rail supérieur",
              "Résistance aux conditions atmosphériques",
              "Option de montage encastré",
            ],
          },
        ],
      },
      colours: {
        overline: "Couleurs & finitions",
        text: "Nos protections extérieures sont disponibles dans un très large choix de couleurs et de finitions. En raison du nombre important de possibilités, nous vous invitons à consulter nos différents nuanciers et catalogues ci-dessous.",
        applies: "",
        downloadCatalogue: "Télécharger le catalogue complet des protections solaires extérieures",
        downloadBso: "Télécharger le nuancier complet des brise-soleil orientables",
        viewAll: "Voir toutes les couleurs",
        collapse: "Réduire",
      },
    },
    products: {
      fenetres: {
        overline: "Produit 01",
        name: "Fenêtres et portes d'entrée",
        tagline: "Châssis aluminium & PVC",
        intro: "Des châssis aux lignes fines, pensés pour maximiser la lumière et l'isolation sans compromis esthétique.",
        features: [
          { t: "Aluminium & PVC", d: "Profilés haute performance, finitions mates et teintes structurées." },
          { t: "Vitrage renforcé", d: "Double ou triple vitrage à isolation thermique et acoustique élevée." },
          { t: "Sur mesure", d: "Chaque ouverture est mesurée, fabriquée et posée par nos équipes." },
        ],
      },
      portes: {
        overline: "Produit 02",
        name: "Portes d'entrée",
        tagline: "Portes d'entrée",
        intro: "Des portes d'entrée qui allient sécurité, isolation et véritable présence architecturale.",
        features: [
          { t: "Sécurité", d: "Serrures multipoints et matériaux robustes pour une protection durable." },
          { t: "Isolation", d: "Panneaux isolants pour un confort thermique et acoustique optimal." },
          { t: "Design sur mesure", d: "Formats, teintes et poignées personnalisés selon votre façade." },
        ],
      },
      garage: {
        overline: "Produit 03",
        name: "Portes de garage",
        tagline: "Sectionnelles & enroulables",
        intro: "Des portes sectionnelles et enroulables au design épuré, motorisées et parfaitement intégrées.",
        features: [
          { t: "Motorisation", d: "Ouverture silencieuse et télécommandée, pensée pour l'usage quotidien." },
          { t: "Gain de place", d: "Sectionnelles verticales qui libèrent l'espace intérieur et extérieur." },
          { t: "Isolation renforcée", d: "Panneaux à rupture de pont thermique pour un garage tempéré." },
        ],
      },
      clotures: {
        overline: "Produit 04",
        name: "Clôtures",
        tagline: "Clôtures & portails",
        intro: "Des clôtures et portails au design contemporain, pour délimiter et sécuriser votre propriété avec élégance.",
        features: [
          { t: "Aluminium durable", d: "Profilés résistants à la corrosion, sans entretien, aux teintes structurées." },
          { t: "Design harmonisé", d: "Des lignes accordées à vos châssis et portes pour une façade cohérente." },
          { t: "Sur mesure", d: "Hauteurs, remplissages et portails adaptés à votre terrain." },
        ],
      },
      accessoires: {
        overline: "Produit 04",
        name: "Protections solaires",
        tagline: "Finitions & quincaillerie",
        intro: "Poignées, seuils, moustiquaires et automatismes : les détails qui parachèvent vos menuiseries.",
        features: [
          { t: "Quincaillerie", d: "Poignées et ferrures sélectionnées pour la durabilité et le geste." },
          { t: "Confort", d: "Moustiquaires, seuils et automatismes pour un usage au quotidien." },
          { t: "Finitions", d: "Des détails accordés au style et aux teintes de vos menuiseries." },
        ],
      },
      pergolas: {
        overline: "Produit 05",
        name: "Pergolas",
        tagline: "Pergolas bioclimatiques",
        intro: "Des pergolas contemporaines pour prolonger l'extérieur et maîtriser la lumière tout au long de l'année.",
        features: [
          { t: "Structure aluminium", d: "Profilés robustes et sans entretien, aux lignes épurées." },
          { t: "Confort maîtrisé", d: "Lames orientables ou toiles pour moduler l'ombre et la ventilation." },
          { t: "Sur mesure", d: "Dimensions, teintes et options adaptées à votre terrasse." },
        ],
      },
    },
    projects: {
      overline: "Réalisations",
      title: "Des chantiers, pas des rendus.",
      lead: "Une sélection de projets livrés à travers la Belgique.",
      type: "Type",
      location: "Localisation",
    },
    manifesto: {
      overline: "Notre approche",
      title: "Le détail avant l'effet.",
      chapters: [
        { n: "01", title: "Des produits sélectionnés", body: "Nous travaillons avec des profilés et vitrages parmi les plus performants du marché, choisis pour leur longévité et leur finesse." },
        { n: "02", title: "Une pose professionnelle", body: "Chaque installation est réalisée par nos équipes, avec une exigence de précision au millimètre et une finition impeccable." },
        { n: "03", title: "Un accompagnement complet", body: "De la prise de mesures au service après-vente, un interlocuteur unique vous guide à chaque étape du projet." },
        { n: "04", title: "La performance énergétique", body: "Isolation thermique et acoustique optimisées pour un confort durable et des factures maîtrisées." },
      ],
    },
    primes: {
      overline: "Primes & aides",
      title: "Jusqu'à 50 % de primes.",
      lead: "Le remplacement de menuiseries peut, selon votre situation, ouvrir droit à des primes rénovation et énergie. Vision+ vous accompagne dans vos démarches.",
      disclaimerLabel: "Information à confirmer",
      disclaimer: "Les conditions, montants et plafonds officiels seront précisés ici une fois vérifiés auprès des régions compétentes. Contenu indicatif — à confirmer.",
      steps: [
        { n: "01", t: "Vérification de l'éligibilité", d: "Nous étudions ensemble votre situation et le type de travaux envisagés. [À confirmer]" },
        { n: "02", t: "Estimation des primes", d: "Nous estimons le montant potentiel des primes applicables à votre projet. [À confirmer]" },
        { n: "03", t: "Accompagnement au dossier", d: "Nous vous aidons à constituer et introduire votre demande. [À confirmer]" },
      ],
      cta: "Prendre rendez-vous",
    },
    about: {
      overline: "À propos",
      title: "Vision+, la menuiserie sans compromis.",
      paragraphs: [
        "Vision+ est une entreprise belge spécialisée dans la menuiserie extérieure haut de gamme : châssis, portes d'entrée, portes de garage et coulissants.",
        "Notre conviction est simple : une menuiserie réussie se remarque à peine. Elle s'efface derrière l'architecture, laisse entrer la lumière et protège durablement. C'est cette justesse que nous poursuivons sur chaque chantier.",
      ],
    },
    contact: {
      overline: "Parlons de votre projet",
      title: "Demandez votre devis.",
      lead: "Décrivez votre projet, nous revenons vers vous sous 48 heures.",
      name: "Nom complet",
      email: "Adresse e-mail",
      phone: "Téléphone",
      project: "Votre projet",
      send: "Envoyer la demande",
      success: "Merci — votre demande a bien été enregistrée.",
    },
    rdv: {
      overline: "Prendre rendez-vous",
      title: "Demandez votre rendez-vous.",
      lead: "Nous vous recontacterons sous 24 h pour confirmer.",
      dateLabel: "Date souhaitée",
      timeLabel: "Créneau horaire",
      firstName: "Prénom",
      lastName: "Nom",
      email: "Adresse e-mail",
      phone: "Téléphone",
      address: "Adresse du bien concerné",
      comment: "Commentaire (facultatif)",
      submit: "Demander le rendez-vous",
      chooseDate: "Veuillez d'abord sélectionner une date et un créneau.",
      successTitle: "Rendez-vous enregistré",
      successNote: "Nous vous recontacterons pour confirmer votre rendez-vous.",
      on: "le",
      at: "à",
      chooseSlot: "Choisir un créneau",
      chooseDatePlaceholder: "Choisir une date",
      phFirstName: "Jean",
      phLastName: "Dupont",
      phEmail: "jean.dupont@email.com",
      phPhone: "0470 12 34 56",
      phAddress: "Rue de Bruxelles 10, 1000 Bruxelles",
      phComment: "Ajoutez une information complémentaire si nécessaire...",
      errPhone: "Veuillez indiquer votre numéro de téléphone.",
      errDate: "Veuillez choisir une date.",
      errSlot: "Veuillez choisir un créneau horaire.",
      errAddress: "Veuillez indiquer l'adresse du bien.",
    },
    footer: {
      tagline: "Menuiserie architecturale — Belgique",
      contact: "Contact",
      nav: "Navigation",
      rights: "Tous droits réservés.",
      legal: "Mentions légales",
      privacy: "Politique de confidentialité",
    },
  },
  nl: {
    nav: {
      home: "Home",
      products: "Producten",
      windows: "Ramen",
      doors: "Voordeuren",
      garage: "Garagepoorten",
      projects: "Realisaties",
      primes: "Regionale premies",
      about: "Over ons",
      quote: "Afspraak maken",
    },
    hero: {
      overline: "Binnen- en buitenschrijnwerk",
      title1: "Uw visie.",
      title2: "Ons vakmanschap.",
      subtitle: "Schrijnwerk op maat, ontworpen voor uw woning.",
      expertisesTitle: "Onze expertises",
      cta1: "Ontdek onze oplossingen",
      cta2: "Afspraak maken",
      cta3: "Controleer uw regionale premies in enkele klikken",
      cta3Short: "Ontdek uw regionale premies",
      quoteCta: "Vraag uw offerte aan",
      scroll: "Scroll",
    },
    home: {
      intro: {
        overline: "Het bedrijf",
        title: "Schrijnwerk als architecturaal gebaar.",
        text: "Gevestigd in België ontwerpen, produceren en plaatsen wij schrijnwerk op maat. Eén eis: de juistheid van het detail, van de eerste opmeting tot de laatste plaatsing.",
      },
      categories: {
        overline: "Onze producten",
        title: "Vier expertises, dezelfde precisie.",
        view: "Ontdekken",
      },
      projects: {
        overline: "Realisaties",
        title: "Werven, geen renders.",
        cta: "Alle realisaties bekijken",
      },
      service: {
        overline: "Een complete service",
        title: "Van opmeting tot plaatsing.",
        text: "Wij begeleiden u bij elke stap van uw project, overal in België.",
        steps: [
          { n: "01", title: "Advies & opmeting", text: "Wij bestuderen uw project en voeren de nodige metingen ter plaatse uit." },
          { n: "02", title: "Levering op maat", text: "Oplossingen geselecteerd en geconfigureerd volgens uw woning." },
          { n: "03", title: "Professionele plaatsing", text: "Een verzorgde installatie tot in de afwerking." },
          { n: "04", title: "Overal in België", text: "Wij realiseren uw projecten in de drie gewesten van het land." },
        ],
      },
      primes: {
        overline: "Premies & steun",
        title: "Tot 50 % premies.",
        text: "Afhankelijk van uw situatie kan de vervanging van uw schrijnwerk recht geven op renovatie- en energiepremies.",
        cta: "Premies ontdekken",
      },
      finalCta: {
        overline: "Uw project",
        title: "Nu, praten we over uw project.",
        text: "Ontvang een offerte op maat binnen 48 uur.",
        cta: "Afspraak maken",
        heading: "Een project in gedachten?",
        subtitle: "Laten we erover praten en samen de oplossing vinden die bij uw woning past.",
        primary: "Offerte aanvragen",
        secondary: "Afspraak maken",
      },
    },
    productsPage: {
      overline: "Producten",
      title: "Een architecturaal aanbod.",
      lead: "Ramen, deuren en garagepoorten op maat, gemaakt om te blijven en elke gevel te verfraaien.",
      view: "Bekijk product",
    },
    fenetresPage: {
      hero: {
        overline: "Ramen",
        title1: "Ramen",
        title2: "gemaakt om te blijven.",
        subtitle:
          "Thermische prestaties, akoestisch comfort en strakke lijnen: schrijnwerk ontworpen om uw architectuur te verfraaien en de jaren te trotseren.",
      },
      materials: {
        overline: "De materialen",
        title: "PVC of aluminium, afhankelijk van uw project.",
        text: "Vision+ biedt verschillende oplossingen afhankelijk van uw project, uw architecturale stijl en uw prestatie-eisen. Elk materiaal heeft zijn sterktes — wij helpen u de juiste keuze te maken.",
        pvc: { name: "PVC", desc: "Robuust, isolerend en voordelig. Ideaal voor renovatie én nieuwbouw." },
        alu: { name: "Aluminium", desc: "Fijn, hedendaags en duurzaam. Voor grote openingen en uitgesproken lijnen." },
      },
      systems: {
        overline: "Onze systemen voor ramen en voordeuren",
        title: "Drie systemen, dezelfde eis.",
        specsLabel: "Kenmerken",
        items: [
          {
            name: "Aluplast Ideal 4000",
            material: "PVC",
            glazing: "Dubbele beglazing",
            position:
              "Onze veelzijdige PVC-oplossing, bijzonder geschikt voor renovatie en voor moderne individuele en collectieve woningen.",
            specs: [
              "Profielklasse B",
              "Inbouwdiepte: 70 mm",
              "Structuur: 5 kamers",
              "Beglazing tot 42 mm",
              "Uw = 1,3 W/m²K",
              "Standaardbeglazing: 4/16Ar/4th [Ug=1.1]",
              "2 afdichtingen",
              "Renovatiekader of monoblok mogelijk",
            ],
          },
          {
            name: "Aluplast Ideal 8000",
            material: "PVC",
            glazing: "Driedubbele beglazing",
            position:
              "Onze hoogperformante PVC-oplossing met driedubbele beglazing, voor projecten die thermische isolatie, akoestisch comfort en energie-efficiëntie vooropstellen.",
            specs: [
              "Profielklasse B",
              "Inbouwdiepte: 85 mm",
              "Structuur: 6 kamers",
              "Beglazing tot 52 mm",
              "Uw = 0,74 W/m²K",
              "Standaardbeglazing: 4th/18Ar/4/18Ar/4th [Ug=0,5]",
              "3 afdichtingen",
              "Driedubbele beglazing",
            ],
          },
          {
            name: "Aluprof MB-86N",
            material: "Aluminium",
            glazing: "Driedubbele beglazing",
            position:
              "Onze premium aluminiumoplossing met driedubbele beglazing, die hedendaagse architecturale esthetiek combineert met uitstekende thermische prestaties.",
            specs: [
              "Inbouwdiepte: 77 mm",
              "Structuur: 3 kamers",
              "Beglazing tot 70 mm",
              "Uw = 0,77 W/m²K",
              "Standaardbeglazing: 4th/18Ar/4/18Ar/4th [Ug=0,5]",
              "2 afdichtingen",
              "Driedubbele beglazing",
            ],
          },
        ],
      },
      performance: {
        overline: "Prestatie & comfort",
        title: "Wat echt telt.",
        items: [
          { t: "Thermische isolatie", d: "Meerkamerprofielen en performante beglazing om de warmte binnen te houden." },
          { t: "Akoestische isolatie", d: "Rust in huis, zelfs in een stedelijke omgeving." },
          { t: "Comfort", d: "Overvloedig licht en vlotte bediening in het dagelijks gebruik." },
          { t: "Veiligheid", d: "Beslag en beglazing ontworpen voor duurzame bescherming." },
          { t: "Esthetiek", d: "Fijne lijnen die verdwijnen achter de architectuur." },
        ],
      },
      colours: {
        overline: "Kleuren & afwerkingen",
        title: "Afgestemd op uw architectuur.",
        text: "Afhankelijk van het gekozen systeem zijn verschillende kleuren en afwerkingen beschikbaar om uw ramen af te stemmen op uw gevel en interieur.",
        applies: "Beschikbare afwerkingen voor de PVC-systemen Aluplast (Ideal 4000 & 8000).",
        refPlaceholder: "Ref. te bevestigen",
        placeholderNote: "Indicatief overzicht — het officiële Aluplast-kleurenwaaier wordt binnenkort geïntegreerd.",
        download: "Download het volledige kleurenwaaier",
        viewAll: "Alle kleuren bekijken",
        collapse: "Minder tonen",
      },
      realisations: { overline: "Realisaties", title: "Onze ramen in situatie.", cta: "Alle realisaties bekijken" },
      cta: { title: "Praten we over uw project.", button: "Afspraak maken" },
    },
    garagePage: {
      systems: {
        overline: "Onze garagepoortsystemen",
        specsLabel: "Kenmerken",
        items: [
          {
            name: "Sectionale garagepoort",
            material: "INFINITI",
            glazing: "",
            position:
              "Geïsoleerde sectionale poort met paneel van 40 mm, beschikbaar in de uitvoeringen Quiet, Safe en Premium.",
            specs: [
              "Beschikbare opties: Quiet, Safe, Premium",
              "Paneeldikte: 40 mm",
              "Beschikbaar in uitvoering: standaard en thermisch",
              "Maximaal gewicht: 130 kg",
              "Minimale afmetingen van de zijhoeken: 90 mm",
              "Minimale afmetingen van de latei: 105 mm",
              "Panelen beschikbaar met glad oppervlak of houtnerfstructuur, met lage en hoge groeven, gelakt volgens de RAL-kleurenkaart of geplastificeerd",
              "Aanbevolen voor installatie in kleine garages",
              "Snelle installatie zonder speciale veren",
              "Mechanisme met dubbele veer",
            ],
          },
          {
            name: "Openslaande garagepoorten",
            material: "PRESTO",
            glazing: "",
            position:
              "Openslaande poorten in robuust staal, gevuld met een isolerend paneel van 40 mm polyurethaan.",
            specs: [
              "Stalen profiel: 1,5 mm",
              "Vulling met een sectionaal poortpaneel van 40 mm",
              "Isolatie van het paneel met polyurethaanschuim",
              "Stalen of automatische dorpel",
              "Profielen gelakt in de kleuren van de RAL-kleurenkaart",
            ],
          },
          {
            name: "Oprolbare garagepoort",
            material: "BALUPROF",
            glazing: "",
            position:
              "Compacte oprolbare poort met pantser PA 55 of PA 77, met buis- of zijmotor.",
            specs: [
              "Pantser: PA 55 of PA 77",
              "Keuze uit buis- of zijmotor",
              "Poort in een kast of op consoles",
              "Bescherming tegen ongecontroleerd vallen van het pantser (traagheidsrem) standaard",
              "Optische veiligheidslijst of foto-elektrische cellen optioneel",
              "Ruim kleurengamma voor de kast, de geleiders en het pantser",
              "Lamellen met kijkvensters en ventilatie optioneel",
              "Noodbediening standaard",
            ],
          },
        ],
      },
      colours: {
        overline: "Kleuren & afwerkingen",
        text: "Afhankelijk van het gekozen systeem zijn verschillende kleuren en afwerkingen beschikbaar om uw garage af te stemmen op uw gevel en uw ramen.",
        applies: "Beschikbare afwerkingen voor sectionale garagepoortsystemen.",
        download: "Download het volledige kleurenwaaier",
        viewAll: "Alle kleuren bekijken",
        collapse: "Minder tonen",
      },
    },
    cloturesPage: {
      systems: {
        overline: "Enkele voorbeelden van onze omheiningen",
        specsLabel: "Kenmerken",
        items: [
          {
            name: "Lugano",
            material: "Standaard omheiningen",
            glazing: "",
            position:
              "Standaard omheining in staal, vulling motief EK.20.102. Loopdeur, schuifpoort of dubbele vleugelpoort, en overspanning.",
            specs: [
              "Vulmotief: EK.20.102",
              "Materiaal: staal",
              "Omheiningssysteem: loopdeur, schuifpoort of dubbele vleugelpoort, overspanning",
              "Afmeting van de palen: 80 × 80 ; 100 × 100 mm (loopdeur + poort)",
              "Kaderprofielen: 80 × 40 mm",
              "Vulprofielen: 80 × 20 mm",
              "Kleuren: RAL – textuur",
            ],
          },
          {
            name: "Maulazzo",
            material: "Elegante omheiningen",
            glazing: "",
            position:
              "Elegante omheining in aluminium, paneel EK.20.108 horizontaal of verticaal plaatsbaar.",
            specs: [
              "Vulmodel: EK.20.108",
              "Materiaal: aluminium",
              "Omheiningssysteem: loopdeur, schuifpoort of dubbele vleugelpoort, overspanning",
              "Afmeting van de palen: 80 × 80 ; 100 × 100 mm (loopdeur + poort)",
              "Kaderprofielen: 58 × 44 mm",
              "Vulprofielen: aluminium paneel, verbinding met lamel, groef van 200 mm",
              "Het paneel kan horizontaal of verticaal worden geplaatst",
              "RAL-kleuren: matte en getextureerde kleuren",
              "Winchester aluminium panelen en Bicolor-optie beschikbaar",
            ],
          },
          {
            name: "Bolsena",
            material: "Prestige-omheiningen",
            glazing: "",
            position:
              "Prestige-omheining in staal, vulling EK.20.110 met gevarieerde profielen.",
            specs: [
              "Vulmodel: EK.20.110",
              "Materiaal: staal",
              "Omheiningssysteem: loopdeur, schuifpoort of dubbele vleugelpoort, overspanning",
              "Afmeting van de palen: 80 × 80 ; 100 × 100 mm (loopdeur + poort)",
              "Kaderprofielen: 60 × 40 mm",
              "Vulprofielen: 80 × 20 ; 60 × 30 ; 30 × 20 mm",
              "Kleuren: RAL – textuur",
            ],
          },
        ],
      },
      colours: {
        overline: "Kleuren & afwerkingen",
        text: "Onze omheiningen zijn beschikbaar in een zeer ruime keuze aan kleuren en afwerkingen. Vanwege het grote aantal mogelijkheden nodigen wij u uit om onze volledige kleurenwaaier en onze volledige catalogus te raadplegen om alle beschikbare tinten te ontdekken.",
        applies: "",
        download: "Download het volledige kleurenwaaier",
        downloadCatalogue: "Download de volledige catalogus",
        viewAll: "Alle kleuren bekijken",
        collapse: "Minder tonen",
      },
    },
    accessoiresPage: {
      systems: {
        overline: "Onze systemen voor buitenzonwering",
        specsLabel: "Kenmerken",
        items: [
          {
            name: "Rolluik",
            material: "SK-45",
            glazing: "",
            position:
              "Dankzij de universele vorm van de kast kan het rolluik zowel rechtstreeks op de gevel van een gebouw als in een nis van het raamkozijn worden gemonteerd.",
            specs: [
              "Type pantser: aluminium gevuld met polyurethaanschuim of PVC",
              "Inspectie aan de buitenzijde",
              "Geleiders: aluminium",
              "Bestand tegen weersinvloeden",
              "Minimale breedte van de kast: 400 mm",
            ],
          },
          {
            name: "Screens",
            material: "FME-90",
            glazing: "",
            position:
              "Voornamelijk gebruikt om de hoeveelheid licht en warmte die de ruimtes binnenkomt doeltreffend te verminderen; er is geen ruimte tussen het doek van de screen en de geleiders.",
            specs: [
              "Type screen: ZIP",
              "Type kast: renovatie, omgekeerde renovatie (contra) of ingebouwd",
              "Motor: bedraad of radiografisch",
              "Materiaal: glasvezel + PVC",
              "Maximale afmetingen (B × H): 6000 × 5000 mm",
              "Maximale oppervlakte: 18 m²",
              "Kast en rails gepoedercoat in de kleuren van de RAL-kleurenkaart",
            ],
          },
          {
            name: "Zonwering met kantelbare lamellen",
            material: "BSO",
            glazing: "",
            position:
              "Het kan eenvoudig in de meeste woningen worden geïnstalleerd, in een adaptief of ingebouwd systeem.",
            specs: [
              "Inspectieluik: geen",
              "Geleiders: aluminium",
              "Hoekprofielen / bovenste railsteunen",
              "Bestand tegen weersomstandigheden",
              "Optie voor ingebouwde montage",
            ],
          },
        ],
      },
      colours: {
        overline: "Kleuren & afwerkingen",
        text: "Onze buitenzonwering is beschikbaar in een zeer ruime keuze aan kleuren en afwerkingen. Vanwege het grote aantal mogelijkheden nodigen wij u uit om onze verschillende kleurenwaaiers en catalogi hieronder te raadplegen.",
        applies: "",
        downloadCatalogue: "Download de volledige catalogus van de buitenzonwering",
        downloadBso: "Download het volledige kleurenwaaier van de kantelbare zonwering",
        viewAll: "Alle kleuren bekijken",
        collapse: "Minder tonen",
      },
    },
    products: {
      fenetres: {
        overline: "Product 01",
        name: "Ramen en voordeuren",
        tagline: "Aluminium & PVC profielen",
        intro: "Ramen met fijne lijnen, ontworpen om licht en isolatie te maximaliseren zonder esthetisch compromis.",
        features: [
          { t: "Aluminium & PVC", d: "Hoogwaardige profielen, matte afwerking en gestructureerde tinten." },
          { t: "Versterkte beglazing", d: "Dubbele of driedubbele beglazing met hoge thermische en akoestische isolatie." },
          { t: "Op maat", d: "Elke opening wordt opgemeten, geproduceerd en geplaatst door onze teams." },
        ],
      },
      portes: {
        overline: "Product 02",
        name: "Voordeuren",
        tagline: "Voordeuren",
        intro: "Voordeuren die veiligheid, isolatie en echte architecturale aanwezigheid verenigen.",
        features: [
          { t: "Veiligheid", d: "Meerpuntssloten en robuuste materialen voor duurzame bescherming." },
          { t: "Isolatie", d: "Isolerende panelen voor optimaal thermisch en akoestisch comfort." },
          { t: "Design op maat", d: "Formaten, tinten en handgrepen aangepast aan uw gevel." },
        ],
      },
      garage: {
        overline: "Product 03",
        name: "Garagepoorten",
        tagline: "Sectionaal & oprolbaar",
        intro: "Sectionaal- en oprolpoorten met strak design, gemotoriseerd en perfect geïntegreerd.",
        features: [
          { t: "Motorisatie", d: "Stille, afstandsbediende opening, ontworpen voor dagelijks gebruik." },
          { t: "Ruimtewinst", d: "Verticale sectionaalpoorten die binnen- en buitenruimte vrijmaken." },
          { t: "Versterkte isolatie", d: "Panelen met thermische onderbreking voor een getemperde garage." },
        ],
      },
      clotures: {
        overline: "Product 04",
        name: "Omheiningen",
        tagline: "Omheiningen & poorten",
        intro: "Omheiningen en poorten met hedendaags design, om uw eigendom elegant af te bakenen en te beveiligen.",
        features: [
          { t: "Duurzaam aluminium", d: "Corrosiebestendige, onderhoudsvrije profielen met gestructureerde tinten." },
          { t: "Afgestemd design", d: "Lijnen afgestemd op uw ramen en deuren voor een coherente gevel." },
          { t: "Op maat", d: "Hoogtes, vullingen en poorten aangepast aan uw terrein." },
        ],
      },
      accessoires: {
        overline: "Product 04",
        name: "Zonwering",
        tagline: "Afwerking & beslag",
        intro: "Handgrepen, dorpels, horren en automatisering: de details die uw schrijnwerk vervolledigen.",
        features: [
          { t: "Beslag", d: "Handgrepen en beslag geselecteerd op duurzaamheid en gebruik." },
          { t: "Comfort", d: "Horren, dorpels en automatisering voor dagelijks gebruik." },
          { t: "Afwerking", d: "Details afgestemd op de stijl en tinten van uw schrijnwerk." },
        ],
      },
      pergolas: {
        overline: "Product 05",
        name: "Pergola's",
        tagline: "Bioklimatische pergola's",
        intro: "Hedendaagse pergola's om het buitenleven te verlengen en het licht het hele jaar door te beheersen.",
        features: [
          { t: "Aluminium structuur", d: "Robuuste, onderhoudsvrije profielen met strakke lijnen." },
          { t: "Beheerst comfort", d: "Oriënteerbare lamellen of doeken om schaduw en ventilatie te regelen." },
          { t: "Op maat", d: "Afmetingen, tinten en opties aangepast aan uw terras." },
        ],
      },
    },
    projects: {
      overline: "Realisaties",
      title: "Werven, geen renders.",
      lead: "Een selectie van opgeleverde projecten in heel België.",
      type: "Type",
      location: "Locatie",
    },
    manifesto: {
      overline: "Onze aanpak",
      title: "Detail vóór effect.",
      chapters: [
        { n: "01", title: "Geselecteerde producten", body: "Wij werken met profielen en beglazing die tot de meest performante op de markt behoren, gekozen voor duurzaamheid en verfijning." },
        { n: "02", title: "Professionele plaatsing", body: "Elke installatie wordt uitgevoerd door onze teams, met millimeterprecisie en een onberispelijke afwerking." },
        { n: "03", title: "Volledige begeleiding", body: "Van opmeting tot naservice begeleidt één aanspreekpunt u door elke fase van het project." },
        { n: "04", title: "Energieprestatie", body: "Geoptimaliseerde thermische en akoestische isolatie voor blijvend comfort en beheerste facturen." },
      ],
    },
    primes: {
      overline: "Premies & steun",
      title: "Tot 50 % premies.",
      lead: "De vervanging van schrijnwerk kan, afhankelijk van uw situatie, recht geven op renovatie- en energiepremies. Vision+ begeleidt u bij uw aanvraag.",
      disclaimerLabel: "Informatie te bevestigen",
      disclaimer: "De officiële voorwaarden, bedragen en plafonds worden hier vermeld zodra ze bij de bevoegde gewesten zijn geverifieerd. Indicatieve inhoud — te bevestigen.",
      steps: [
        { n: "01", t: "Controle van de voorwaarden", d: "We bekijken samen uw situatie en het type geplande werken. [Te bevestigen]" },
        { n: "02", t: "Raming van de premies", d: "We ramen het potentiële bedrag van de toepasbare premies voor uw project. [Te bevestigen]" },
        { n: "03", t: "Begeleiding van het dossier", d: "We helpen u bij het samenstellen en indienen van uw aanvraag. [Te bevestigen]" },
      ],
      cta: "Afspraak maken",
    },
    about: {
      overline: "Over ons",
      title: "Vision+, schrijnwerk zonder compromis.",
      paragraphs: [
        "Vision+ is een Belgisch bedrijf gespecialiseerd in hoogwaardig buitenschrijnwerk: ramen, voordeuren, garagepoorten en schuifsystemen.",
        "Onze overtuiging is eenvoudig: geslaagd schrijnwerk valt nauwelijks op. Het verdwijnt achter de architectuur, laat licht binnen en beschermt duurzaam. Die juistheid nastreven wij op elke werf.",
      ],
    },
    contact: {
      overline: "Praten we over uw project",
      title: "Vraag uw offerte aan.",
      lead: "Beschrijf uw project, wij nemen binnen 48 uur contact op.",
      name: "Volledige naam",
      email: "E-mailadres",
      phone: "Telefoon",
      project: "Uw project",
      send: "Aanvraag versturen",
      success: "Bedankt — uw aanvraag is goed geregistreerd.",
    },
    rdv: {
      overline: "Afspraak maken",
      title: "Boek uw afspraak.",
      lead: "Kies een datum en tijdslot, wij nemen contact op ter bevestiging.",
      dateLabel: "Gewenste datum",
      timeLabel: "Tijdslot",
      firstName: "Voornaam",
      lastName: "Naam",
      email: "E-mailadres",
      phone: "Telefoon",
      address: "Adres van het betrokken pand",
      comment: "Opmerking (optioneel)",
      submit: "Afspraak bevestigen",
      chooseDate: "Selecteer eerst een datum en een tijdslot.",
      successTitle: "Afspraak geregistreerd",
      successNote: "Wij nemen contact met u op om uw afspraak te bevestigen.",
      on: "op",
      at: "om",
      chooseSlot: "Kies een tijdslot",
      chooseDatePlaceholder: "Kies een datum",
      phFirstName: "Jan",
      phLastName: "Janssens",
      phEmail: "jan.janssens@email.com",
      phPhone: "0470 12 34 56",
      phAddress: "Brusselsestraat 10, 1000 Brussel",
      phComment: "Voeg indien nodig aanvullende informatie toe...",
      errPhone: "Gelieve uw telefoonnummer in te vullen.",
      errDate: "Gelieve een datum te kiezen.",
      errSlot: "Gelieve een tijdslot te kiezen.",
      errAddress: "Gelieve het adres van het pand in te vullen.",
    },
    footer: {
      tagline: "Architecturaal schrijnwerk — België",
      contact: "Contact",
      nav: "Navigatie",
      rights: "Alle rechten voorbehouden.",
      legal: "Wettelijke vermeldingen",
      privacy: "Privacybeleid",
    },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState("fr");
  const t = translations[lang];
  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
